import { isOsFileDrag } from "@/dnd/osFileDrop";
import { cn } from "@/utils";
import {
  dataTransferItemsHaveDirectory,
  traverseDataTransferItems,
  type FolderUploadTree,
} from "@/utils/traverseDataTransferItems";
import { Upload } from "lucide-react";
import React, { forwardRef, useCallback, useRef, useState } from "react";

export interface WorkspaceDropPayload {
  /** Top-level files dropped without an enclosing folder. */
  files: File[];
  /**
   * Set when the drop included at least one directory. Contains the
   * full recursive expansion of every dropped folder.
   */
  tree?: FolderUploadTree;
}

interface WorkspaceDropZoneProps {
  onDrop: (payload: WorkspaceDropPayload) => void;
  children: React.ReactNode;
  className?: string;
  /**
   * Overlay copy. Zones nest (the composer's sits inside the page's), and each
   * one names its own destination so the user can see where a drop will land.
   */
  label?: string;
  /** Compact overlay for small zones like the composer. */
  compact?: boolean;
}

/**
 * OS-file drop zone. Every drag handler calls `stopPropagation`, so a nested
 * zone claims the drop before an ancestor zone sees it — that is what lets the
 * composer capture "attach to message" while the page around it uploads to the
 * workspace. `CanvasRailOsDropZone` mirrors these drag-counter semantics.
 */
export const WorkspaceDropZone = forwardRef<
  HTMLDivElement,
  WorkspaceDropZoneProps
>(function WorkspaceDropZone(
  { onDrop, children, className, label = "Drop files to upload", compact },
  ref,
) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    // isOsFileDrag (not a bare types check): a native drag of an in-app
    // <img> also advertises "Files" — it must not light up the upload zone.
    if (isOsFileDrag(e.nativeEvent)) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      // Swallow internal-drag drops (preventDefault above already ran, so
      // the browser won't navigate to a dropped image) without uploading.
      if (!isOsFileDrag(e.nativeEvent)) return;

      // Snapshot both `items` and `files` synchronously — `DataTransferItem`
      // references and the `webkitGetAsEntry()` return value are
      // invalidated as soon as the handler yields. Same gotcha noted in
      // `useCopyPaste.ts`.
      const items = e.dataTransfer.items
        ? Array.from(e.dataTransfer.items)
        : [];
      const files = Array.from(e.dataTransfer.files);

      const hasDirectory =
        items.length > 0 && dataTransferItemsHaveDirectory(items);

      if (!hasDirectory) {
        if (files.length > 0) onDrop({ files });
        return;
      }

      // Directory drop: walk the entry tree asynchronously. Items must
      // already be captured (above) — `traverseDataTransferItems`
      // operates on the snapshot.
      void (async () => {
        const tree = await traverseDataTransferItems(items);
        const totalCount =
          tree.files.length + tree.looseFiles.length + tree.folders.length;
        if (totalCount === 0) return;
        onDrop({ files: tree.looseFiles, tree });
      })();
    },
    [onDrop],
  );

  return (
    <div
      ref={ref}
      className={cn("relative", className)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}
      {isDragging && (
        <div
          className={cn(
            "absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm",
            compact && "rounded-xl",
          )}
        >
          <div
            className={cn(
              "flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-foreground/30 bg-surface-card/90 shadow-2xl",
              compact ? "gap-2 px-6 py-4" : "px-12 py-10",
            )}
          >
            <Upload
              className={cn(
                "text-foreground/40",
                compact ? "h-6 w-6" : "h-10 w-10",
              )}
            />
            <p className="text-sm font-medium text-foreground/70">{label}</p>
          </div>
        </div>
      )}
    </div>
  );
});
